import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, toBaseCurrency, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Plus, Ban, Trash2 } from "lucide-react";

const emptyForm = { name: "", category: "equipamento", acquisition_date: "", acquisition_cost: "", useful_life_years: "4", residual_value: "", location: "", responsible: "" };

export default function AssetsPage() {
  const { token, isAdmin } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAssets(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAssets = async () => {
    try {
      const res = await axios.get(`${API_URL}/assets`, { headers, withCredentials: true });
      setAssets(res.data);
    } catch { toast.error("Erro ao carregar ativos"); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/assets`, {
        ...form,
        acquisition_cost: toBaseCurrency(form.acquisition_cost),
        residual_value: toBaseCurrency(form.residual_value),
        useful_life_years: parseInt(form.useful_life_years) || 4,
      }, { headers, withCredentials: true });
      toast.success("Ativo registado");
      setModal(false); setForm(emptyForm);
      fetchAssets();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao registar"); }
  };

  const dispose = async (a) => {
    if (!window.confirm(`Abater o ativo "${a.name}"?`)) return;
    try {
      await axios.put(`${API_URL}/assets/${a.asset_id}/dispose`, {}, { headers, withCredentials: true });
      toast.success("Ativo abatido");
      fetchAssets();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remover o ativo "${a.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/assets/${a.asset_id}`, { headers, withCredentials: true });
      toast.success("Removido");
      fetchAssets();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const totalBook = assets.filter((a) => a.status === "active").reduce((s, a) => s + (a.book_value || 0), 0);

  return (
    <div className="space-y-6" data-testid="assets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Ativos / Imobilizado</h1>
          <p className="text-muted-foreground">Equipamentos e bens com depreciação linear automática</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setModal(true); }} data-testid="add-asset-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Ativo
        </Button>
      </div>

      <Card><CardContent className="p-4">
        <p className="text-xs text-muted-foreground">Valor Líquido Contabilístico (ativos ativos)</p>
        <p className="text-2xl font-bold font-mono" data-testid="total-book-value">{formatCurrency(totalBook)}</p>
      </CardContent></Card>

      {loading ? <p className="text-muted-foreground">A carregar...</p> :
        assets.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">
            <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Nenhum ativo registado
          </CardContent></Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {assets.map((a) => (
              <Card key={a.asset_id} data-testid={`asset-card-${a.asset_id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{a.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{a.category} · adquirido {a.acquisition_date}</p>
                    </div>
                    <Badge className={a.status === "active" ? "bg-emerald-500/20 text-emerald-600" : "bg-red-500/20 text-red-600"}>
                      {a.status === "active" ? (a.fully_depreciated ? "Depreciado" : "Ativo") : "Abatido"}
                    </Badge>
                  </div>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Custo</span><span className="font-mono">{formatCurrency(a.acquisition_cost)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Dep. acumulada</span><span className="font-mono text-red-600">-{formatCurrency(a.accumulated_depreciation)}</span></div>
                    <div className="flex justify-between font-bold border-t pt-1"><span>Valor líquido</span><span className="font-mono">{formatCurrency(a.book_value)}</span></div>
                  </div>
                  {a.location && <p className="text-xs text-muted-foreground">Localização: {a.location}{a.responsible ? ` · Resp.: ${a.responsible}` : ""}</p>}
                  <div className="flex gap-2 pt-1">
                    {a.status === "active" && (
                      <Button size="sm" variant="outline" onClick={() => dispose(a)} data-testid={`dispose-asset-${a.asset_id}`}>
                        <Ban className="w-4 h-4 mr-1" /> Abater
                      </Button>
                    )}
                    {isAdmin && (
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(a)}><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo Ativo</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="space-y-1">
              <Label>Nome *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Forno industrial" data-testid="asset-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equipamento">Equipamento</SelectItem>
                    <SelectItem value="mobiliario">Mobiliário</SelectItem>
                    <SelectItem value="viatura">Viatura</SelectItem>
                    <SelectItem value="informatica">Informática</SelectItem>
                    <SelectItem value="edificio">Edifício</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Data de Aquisição *</Label>
                <Input type="date" required value={form.acquisition_date} onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })} data-testid="asset-date-input" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Custo *</Label>
                <Input type="number" step="0.01" min="0.01" required value={form.acquisition_cost} onChange={(e) => setForm({ ...form, acquisition_cost: e.target.value })} data-testid="asset-cost-input" />
              </div>
              <div className="space-y-1">
                <Label>Vida útil (anos)</Label>
                <Input type="number" min="1" value={form.useful_life_years} onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Valor Residual</Label>
                <Input type="number" step="0.01" min="0" value={form.residual_value} onChange={(e) => setForm({ ...form, residual_value: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Localização</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Responsável</Label>
                <Input value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-asset-btn">Registar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
